// ProtectedLayout — wraps owner/staff routes. Verifies auth; admin -> blocked.

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { TopNav, BottomTabBar } from '../components/chrome';
import { Button } from '../components/primitives';
import { useViewport } from '../lib/useViewport';

export function ProtectedLayout() {
  const auth = useAuth();
  const location = useLocation();
  const viewport = useViewport();

  if (auth.status === 'loading') {
    return <FullScreenMessage title="확인 중…" sub="잠시만 기다려 주세요." />;
  }
  if (auth.status === 'unauth' || !auth.me) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (auth.role === 'admin') {
    return <BlockedAdmin onLogout={auth.logout} />;
  }
  if (auth.role !== 'owner' && auth.role !== 'staff') {
    return <BlockedNoMembership onLogout={auth.logout} />;
  }

  const initial =
    auth.me?.user.name?.[0] ?? auth.me?.user.email?.[0]?.toUpperCase() ?? '나';
  const isMobile = viewport === 'mobile';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <TopNav userInitial={initial} onLogout={auth.logout} viewport={viewport} />
      <main
        style={{
          flex: 1,
          maxWidth: 'var(--page-max)',
          width: '100%',
          margin: 0,
          boxSizing: 'border-box',
          minWidth: 0,
          padding:
            'var(--page-pad-top) var(--page-pad-x) var(--page-pad-bottom)',
        }}
      >
        <Outlet />
      </main>
      {/* Phase 6.C-5 — BottomTabBar 는 모바일에서만 노출. 데스크톱은 TopNav 로 충분. */}
      {isMobile && <BottomTabBar />}
    </div>
  );
}

function FullScreenMessage({ title, sub }: { title: string; sub?: string }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        color: 'var(--fg-2)',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 16 }}>{title}</div>
      {sub && <div style={{ color: 'var(--fg-3)', fontSize: 13 }}>{sub}</div>}
    </div>
  );
}

function BlockedAdmin({ onLogout }: { onLogout: () => void }) {
  return (
    <BlockedShell
      title="이 계정은 사장 콘솔을 사용할 수 없습니다"
      message="회사 owner / staff 권한이 필요합니다. 권한 요청 후 다시 로그인해 주세요."
      onLogout={onLogout}
    />
  );
}

function BlockedNoMembership({ onLogout }: { onLogout: () => void }) {
  return (
    <BlockedShell
      title="소속된 회사가 없습니다"
      message="회사 가입 신청이 승인되지 않았거나, 직원 초대를 아직 수락하지 않았습니다."
      onLogout={onLogout}
    />
  );
}

function BlockedShell({
  title,
  message,
  ctaHref,
  ctaLabel,
  onLogout,
}: {
  title: string;
  message: string;
  ctaHref?: string;
  ctaLabel?: string;
  onLogout: () => void;
}) {
  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--bg-page)',
      }}
    >
      <section
        style={{
          maxWidth: 420,
          width: '100%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-weak)',
          borderRadius: 'var(--radius-xl)',
          padding: 28,
          textAlign: 'center',
          boxShadow: 'var(--shadow-md)',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--fg-1)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--fg-3)',
            lineHeight: 1.6,
          }}
        >
          {message}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
            marginTop: 8,
          }}
        >
          {ctaHref && (
            <a href={ctaHref} style={{ textDecoration: 'none' }}>
              <Button variant="strong">{ctaLabel ?? '이동'}</Button>
            </a>
          )}
          <Button variant="secondary" onClick={onLogout}>
            로그아웃
          </Button>
        </div>
      </section>
    </main>
  );
}
